/* =============================================================
   ConsultationFullPage — Зөвлөгөө авах (standalone page)
   Same 3-step flow as ConsultationModal but rendered as a full page
   ============================================================= */
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, Phone, User, Globe, CheckCircle, ExternalLink, Mail, Star, ArrowLeft } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useLocation } from "wouter";

const COUNTRIES = [
  { id: "new-zealand", flag: "🇳🇿", name: "Шинэ Зеланд", nameEn: "New Zealand", trending: true },
  { id: "usa", flag: "🇺🇸", name: "АНУ", nameEn: "USA" },
  { id: "australia", flag: "🇦🇺", name: "Австрали", nameEn: "Australia" },
  { id: "canada", flag: "🇨🇦", name: "Канад", nameEn: "Canada" },
  { id: "schengen", flag: "🇪🇺", name: "Шенген", nameEn: "Schengen" },
  { id: "czech", flag: "🇨🇿", name: "Чех", nameEn: "Czech Republic" },
  { id: "ireland", flag: "🇮🇪", name: "Ирланд", nameEn: "Ireland" },
  { id: "korea", flag: "🇰🇷", name: "Солонгос", nameEn: "South Korea" },
  { id: "uk", flag: "🇬🇧", name: "Англи", nameEn: "UK" },
];

interface Service {
  id: string;
  name: string;
  description: string;
  bullets: string[];
  duration: string;
  price: number;
  qpayLink: string;
  highlight?: "vip" | "work" | "special";
}

const TRAVEL_LINK = "https://paylink.mn/api/s/inv_mrzx7kegbce69b";
const STUDY_LINK = "https://paylink.mn/api/s/inv_mrzx8tr30dd7d5";
const IMMIGRATION_LINK = "https://paylink.mn/api/s/inv_mrzxadzm4dbfd5";
const VIP_LINK = "https://paylink.mn/api/s/inv_mrzxbxln5bf605";

const VIP_SERVICE = (countryName: string): Service => ({
  id: `${countryName.toLowerCase()}-vip`,
  name: `${countryName} VIP уулзалт — Дэлгэрэнгүй үнэлгээ`,
  description: "Дээрхи бүх зөвлөгөөнүүд багтсан иж бүрэн уулзалт",
  bullets: [
    "Таны кэйст дэлгэрэнгүй шинжилгээ хийх",
    "Хууль ёсоор ажиллаж амьдрах боломжийн тойм",
    "Дараагийн алхамуудын нарийвчилсан зааварчилгаа",
  ],
  duration: "2 цаг",
  price: 199000,
  qpayLink: VIP_LINK,
  highlight: "vip" as const,
});

const SERVICES_BY_COUNTRY: Record<string, Service[]> = {
  usa: [
    {
      id: "usa-work-green",
      name: "АНУ ажлын виз, амьдрах эрх авах",
      description: "EB3, EB1-2, NIW, H2B, H1B, O1, L1 болон бусад ангиллаар ажлын виз, ногоон карт авах бүрэн зөвлөгөө",
      bullets: [
        "EB3 Unskilled — өндөр ур чадвар шаардахгүй, гэр бүлээрээ ажлын шугамаар ногоон карт авах",
        "EB NIW — ажил олгогчгүйгээр өөрөөрөө, гэр бүлээрээ ногоон карт авах",
        "EB1-2 — онцгой чадвар эсвэл нэрт профессорт зориулсан ногоон карт",
        "H2B — улирлын ажилчдад зориулсан ажлын виз",
        "H1B — мэргэжлийн ажилчдад зориулсан ажлын виз",
        "O1 — онцгой авьяас чадвартай хүмүүст зориулсан виз",
        "L1 — компанийн дотоод шилжилт хөдөлгөөний виз",
        "Таньд тохирсон ангиллыг тодорхойлох",
      ],
      duration: "60 минут",
      price: 99000,
      qpayLink: "https://paylink.mn/api/s/inv_ms0ubhod5f9c1f",
      highlight: "work" as const,
    },
    {
      id: "usa-travel",
      name: "АНУ аялал B1/B2",
      description: "Аялалын виз мэдүүлэх бүрэн зөвлөгөө",
      bullets: [
        "Анх удаа эсвэл дахин мэдүүлэх стратеги",
        "Бондоо найдвартай буцааж авах арга",
        "DS-160 анкет шинээр үүсгэх",
        "Таны эрсдлийг тодорхойлох",
        "Шаардлагатай материалын жагсаалт",
        "Ярилцлагад бэлдэх зөвлөмж",
        "Зардлын тооцоолол",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: "https://paylink.mn/api/s/inv_mrz8edg071e04d",
    },
    {
      id: "usa-study",
      name: "АНУ оюутны F-1 виз",
      description: "Оюутны виз мэдүүлэх бүрэн зөвлөгөө — хэлний курсээр явах, ажил эрхлэх боломжууд",
      bullets: [
        "Гэрээт сургуулиудад зуучлах",
        "Хэлний курсээр суралцах боломж",
        "Статусаа хадгалах",
        "Хөтөлбөр, анги шилжэхэд анхаарах зүйлс",
        "I-20 болон SEVIS бүртгэл",
        "Ярилцлагад бэлдэх",
        "Ажил эрхлэх боломжууд (OPT/CPT)",
        "Тэтгэлэгт зуучлах",
      ],
      duration: "45 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
    {
      id: "usa-immigration",
      name: "АНУ цагаачлал",
      description: "Цагаачлал болон орогнолын асуудлаар иж бүрэн зөвлөгөө",
      bullets: [
        "Цагаачлалын өмгөөлөгчтэй холбох",
        "Орогнолын асуудал",
        "Ажлын спонсор олоход туслах",
        "Таны онцлогт тохирсон бусад боломжууд",
      ],
      duration: "45 минут",
      price: 159000,
      qpayLink: IMMIGRATION_LINK,
    },
    VIP_SERVICE("АНУ"),
  ],
  australia: [
    {
      id: "au-travel",
      name: "Австрали аяллын виз",
      description: "Visitor visa (subclass 600) зөвлөгөө — татгалзлыг бууруулах арга",
      bullets: [
        "Online profile нээх",
        "Материалын тайлбар",
        "Захидал бичих",
        "Санхүүгийн баталгаа",
        "Аяллын төлөвлөгөө",
        "Татгалзлыг бууруулах арга",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
    {
      id: "au-study",
      name: "Австрали оюутны виз",
      description: "Student visa (subclass 500) зөвлөгөө — хэлний курсээр явах, ажил эрхлэх боломжууд, татгалзлыг бууруулах арга",
      bullets: [
        "Гэрээт сургуулиудад зуучлах",
        "Хэлний курсээр суралцах боломж",
        "Ажил эрхлэх боломжууд",
        "CoE бичиг баримт",
        "Эрүүл мэндийн шалгалт",
        "Тэтгэлэгт зуучлах",
        "Татгалзлыг бууруулах арга",
      ],
      duration: "45 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
    {
      id: "au-immigration",
      name: "Австрали цагаачлал",
      description: "Австрали улсад байнга оршин суух, ажлын визний бүрэн зөвлөгөө",
      bullets: [
        "Skilled Independent visa (subclass 189)",
        "Employer Sponsored visa (subclass 482/186)",
        "Regional Sponsored Migration Scheme (subclass 187)",
        "Цагаачлалын өмгөөлөгчтэй холбох",
        "Таны онцлогт тохирсон ажлын визний төрлийг тодорхойлох",
      ],
      duration: "45 минут",
      price: 159000,
      qpayLink: IMMIGRATION_LINK,
    },
    VIP_SERVICE("Австрали"),
  ],
  canada: [
    {
      id: "ca-travel",
      name: "Канад аяллын виз",
      description: "Visitor visa / eTA зөвлөгөө",
      bullets: [
        "GCKEY profile нээх",
        "Материалын тайлбар",
        "Захидал бичих",
        "Санхүүгийн баталгаа",
        "Аяллын зорилго тайлбарлах",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
    {
      id: "ca-study",
      name: "Канад оюутны виз",
      description: "Study permit зөвлөгөө — хэлний курсээр явах, ажил эрхлэх боломжууд",
      bullets: [
        "Гэрээт сургуулиудад зуучлах",
        "Хэлний курсээр суралцах боломж",
        "Study permit мэдүүлэх",
        "Сургууль сонгох зөвлөгөө",
        "Acceptance letter авах",
        "Биометр бүртгэл",
        "Ажил эрхлэх боломжууд (Co-op/PGWP)",
        "Тэтгэлэгт зуучлах",
      ],
      duration: "45 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
    {
      id: "ca-immigration",
      name: "Канад цагаачлал",
      description: "Express Entry болон бусад цагаачлалын хөтөлбөрийн зөвлөгөө",
      bullets: [
        "Express Entry системийн тайлбар",
        "Таны оноог нэмэгдүүлэх арга",
        "Магадлалыг нэмэгдүүлэх стратеги",
        "Цагаачлалын өмгөөлөгчтэй холбох",
      ],
      duration: "45 минут",
      price: 159000,
      qpayLink: IMMIGRATION_LINK,
    },
    VIP_SERVICE("Канад"),
  ],
  schengen: [
    {
      id: "sch-travel",
      name: "Шенген аяллын виз",
      description: "Шенген бүс нутгийн аяллын виз — татгалзлыг бууруулах арга",
      bullets: [
        "Элчин сайдын газар сонгох",
        "Материал бүрдүүлэх",
        "Цаг захиалах заавар",
        "Татгалзлыг бууруулах арга",
        "Санхүүгийн баталгаа",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
  ],
  czech: [
    {
      id: "cz-work-contract",
      name: "Чехийн хөдөлмөрийн гэрээ",
      description: "Чех улсад ажиллах хөдөлмөрийн гэрээгээр явах бүрэн зөвлөгөө",
      bullets: [
        "Гэрээт үйлдвэр, компаниудад зуучлах",
        "Элчин сайдын газарт цаг захиалах",
        "Материал бүрдүүлэх заавар",
        "Нисэх онгоцны буудлаас тосох",
        "Байр, орон сууцны асуудал",
        "Хөдөлмөрийн зөвшөөрлийн хүсэлт",
      ],
      duration: "60 минут",
      price: 99000,
      qpayLink: "https://paylink.mn/api/s/inv_ms0ubhod5f9c1f",
      highlight: "special" as const,
    },
  ],
  ireland: [
    {
      id: "ie-travel",
      name: "Ирланд аяллын виз",
      description: "Ирланд улсын аяллын виз — татгалзлыг бууруулах арга",
      bullets: [
        "Материал бүрдүүлэх",
        "Санхүүгийн баталгаа",
        "Аяллын зорилго",
        "Татгалзлыг бууруулах арга",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
    {
      id: "ie-study",
      name: "Ирландийн сургалт, ажлын виз",
      description: "Ирланд улсад суралцах болон ажлын виз — ажлын виз, гэр бүлээрээ явах, хэлний курсээр явах боломж",
      bullets: [
        "Гэрээт сургуулиудад зуучлах",
        "Материал бүрдүүлэлт",
        "Давж заалдалт",
        "Төлбөрөө хурдан буцааж авах",
        "Хэлний курсээр суралцах боломж",
        "Ажил эрхлэх боломжууд (20 цаг/7 хоног)",
        "Ажлын виз авах арга зам",
        "Гэр бүлээрээ явах боломж",
        "Татгалзлыг бууруулах арга",
      ],
      duration: "45 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
  ],
  korea: [
    {
      id: "kr-travel",
      name: "Солонгос аяллын виз",
      description: "Солонгос улсын аяллын виз",
      bullets: ["Материал бүрдүүлэх", "Санхүүгийн баталгаа", "Аяллын зорилго"],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
    {
      id: "kr-study",
      name: "Солонгос оюутны виз",
      description: "Солонгос улсад суралцах виз — хэлний курсээр явах, ажил эрхлэх боломжууд",
      bullets: [
        "Гэрээт сургуулиудад зуучлах",
        "Хэлний курсээр суралцах боломж (TOPIK)",
        "Ажил эрхлэх боломжууд",
        "Тэтгэлэгт зуучлах",
      ],
      duration: "45 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
    VIP_SERVICE("Солонгос"),
  ],
  uk: [
    {
      id: "uk-travel",
      name: "Их Британи аяллын виз",
      description: "UK Standard Visitor Visa зөвлөгөө — татгалзлыг бууруулах арга",
      bullets: [
        "Материал бүрдүүлэх",
        "Санхүүгийн баталгаа",
        "Аяллын зорилго",
        "Татгалзлыг бууруулах арга",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
    {
      id: "uk-study",
      name: "Их Британи оюутны виз",
      description: "UK Student Visa зөвлөгөө — хэлний курсээр явах, ажил эрхлэх боломжууд",
      bullets: [
        "Гэрээт сургуулиудад зуучлах",
        "Хэлний курсээр суралцах боломж",
        "Ажил эрхлэх боломжууд (20 цаг/7 хоног)",
        "CAS бичиг баримт",
        "Тэтгэлэгт зуучлах",
        "Татгалзлыг бууруулах арга",
      ],
      duration: "45 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
  ],
  "new-zealand": [
    {
      id: "nz-skilled",
      name: "Шинэ Зеланд — Мэргэжлээрээ ажиллах",
      description: "Мэргэжлийн боломжийг тодорхойлж, эртнээс бэлдэж эхлэх",
      bullets: [
        "Таны боломжийг мэргэжлийн бага тодорхойлно",
        "Эртнээс таныг бэлдэж эхлэх",
        "Хуулийн фирмтэй хамтарч ажиллах",
        "Skilled Migrant Category ба Essential Skills визний шаардлага",
        "Мэргэжлийг хүлээн зөвшөөрүүлэх арга",
        "Таньд тохирсон визийг тодорхойлох",
      ],
      duration: "45 минут",
      price: 69000,
      qpayLink: "https://paylink.mn/api/s/inv_mrzynhjf4a66af",
      highlight: "special" as const,
    },
    {
      id: "nz-travel",
      name: "Шинэ Зеланд аяллын виз",
      description: "Visitor Visa зөвлөгөө",
      bullets: ["Материал бүрдүүлэх", "Санхүүгийн баталгаа", "Аяллын зорилго"],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
    {
      id: "nz-study",
      name: "Шинэ Зеланд оюутны виз",
      description: "Student Visa зөвлөгөө — хэлний курсээр явах, ажил эрхлэх боломжууд",
      bullets: [
        "Гэрээт сургуулиудад зуучлах",
        "Хэлний курсээр суралцах боломж",
        "Ажил эрхлэх боломжууд",
        "Тэтгэлэгт зуучлах",
      ],
      duration: "45 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
  ],
};

const STEP_LABELS = ["Мэдээлэл", "Улс", "Үйлчилгээ"];

const LS_KEY = "udm_contact_info";

export default function ConsultationFullPage() {
  const [, navigate] = useLocation();
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [errors, setErrors] = useState<{ name?: string; phone?: string; email?: string }>({});

  // Load saved contact info from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(LS_KEY);
      if (saved) {
        const { name: n, phone: p, email: e } = JSON.parse(saved);
        if (n && p && e) {
          setName(n);
          setPhone(p);
          setEmail(e);
          setStep(2); // Skip step 1
        }
      }
    } catch {}
  }, []);

  const validateStep1 = () => {
    const newErrors: { name?: string; phone?: string; email?: string } = {};
    if (!name.trim()) newErrors.name = "Нэрээ оруулна уу";
    if (!phone.trim()) newErrors.phone = "Утасны дугаар оруулна уу";
    else if (!/^[0-9]{8}$/.test(phone.replace(/\s/g, ""))) newErrors.phone = "8 оронтой дугаар оруулна уу";
    if (!email.trim()) newErrors.email = "Мэйл хаягаа оруулна уу";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = "Зөв мэйл хаяг оруулна уу";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const notifyLead = trpc.callbacks.notifyConsultationLead.useMutation();

  const handleStep1Next = () => {
    if (validateStep1()) {
      // Save to localStorage so user doesn't need to re-enter next time
      try {
        localStorage.setItem(LS_KEY, JSON.stringify({ name, phone, email }));
      } catch {}
      notifyLead.mutate({ name, phone, email });
      setStep(2);
    }
  };

  const handleCountrySelect = (countryId: string) => {
    setSelectedCountry(countryId);
    setSelectedService(null);
    setStep(3);
  };

  const services = selectedCountry ? SERVICES_BY_COUNTRY[selectedCountry] || [] : [];
  const country = COUNTRIES.find((c) => c.id === selectedCountry);
  const progressPct = step === 1 ? 33 : step === 2 ? 66 : 100;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-start pt-24 pb-16 px-4"
      style={{ background: "linear-gradient(135deg, #0D0A2E 0%, #1a1040 60%, #0D0A2E 100%)" }}
    >
      {/* Background glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{ background: "#7C3AED" }} />
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full opacity-8 blur-3xl" style={{ background: "#4F46E5" }} />
      </div>

      {/* Back button */}
      <div className="w-full max-w-lg mb-4 relative z-10">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm transition-colors hover:text-white"
          style={{ color: "rgba(167,139,250,0.7)" }}
        >
          <ArrowLeft size={15} />
          Нүүр хуудас руу буцах
        </button>
      </div>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl z-10"
        style={{ background: "linear-gradient(135deg, #0D0A2E 0%, #1a1040 100%)", border: "1px solid rgba(124,58,237,0.3)" }}
      >
        {/* Progress bar */}
        <div className="h-1 w-full" style={{ background: "rgba(124,58,237,0.15)" }}>
          <motion.div
            className="h-full rounded-full"
            style={{ background: "linear-gradient(90deg, #7C3AED, #4F46E5, #3B82F6)" }}
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          />
        </div>

        {/* Header */}
        <div className="px-6 pt-5 pb-4" style={{ borderBottom: "1px solid rgba(124,58,237,0.2)" }}>
          <div className="mb-4">
            <h2 className="text-xl font-bold text-white">Зөвлөгөө авах</h2>
            <p className="text-sm mt-0.5" style={{ color: "rgba(167,139,250,0.8)" }}>
              {step === 1 && "Таны мэдээлэл"}
              {step === 2 && "Улс сонгоно уу"}
              {step === 3 && (country ? `${country.flag} ${country.name} — Үйлчилгээ сонгоно уу` : "Үйлчилгээ сонгоно уу")}
            </p>
          </div>

          {/* Step indicators */}
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2 flex-1">
                <div className="flex items-center gap-1.5">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all flex-shrink-0"
                    style={{
                      background: step >= s ? "linear-gradient(135deg, #7C3AED, #4F46E5)" : "rgba(124,58,237,0.15)",
                      color: step >= s ? "white" : "rgba(167,139,250,0.4)",
                      border: step === s ? "2px solid #A78BFA" : "2px solid transparent",
                    }}
                  >
                    {step > s ? <CheckCircle size={12} /> : s}
                  </div>
                  <span className="text-xs hidden sm:block" style={{ color: step >= s ? "rgba(167,139,250,0.9)" : "rgba(167,139,250,0.35)" }}>
                    {STEP_LABELS[s - 1]}
                  </span>
                </div>
                {s < 3 && (
                  <div
                    className="h-px flex-1 rounded"
                    style={{ background: step > s ? "linear-gradient(90deg, #7C3AED, #4F46E5)" : "rgba(124,58,237,0.2)" }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <AnimatePresence mode="wait">

            {/* Step 1: Contact info */}
            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: "rgba(167,139,250,0.9)" }}>
                    <User size={13} className="inline mr-1.5" />
                    Таны нэр <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Жишээ: Болд Баатар"
                    className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all"
                    style={{ background: "rgba(124,58,237,0.1)", border: errors.name ? "1px solid #EF4444" : "1px solid rgba(124,58,237,0.3)" }}
                    onFocus={(e) => (e.target.style.border = "1px solid rgba(124,58,237,0.8)")}
                    onBlur={(e) => (e.target.style.border = errors.name ? "1px solid #EF4444" : "1px solid rgba(124,58,237,0.3)")}
                  />
                  {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: "rgba(167,139,250,0.9)" }}>
                    <Phone size={13} className="inline mr-1.5" />
                    Утасны дугаар <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="8 оронтой дугаар"
                    maxLength={8}
                    className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all"
                    style={{ background: "rgba(124,58,237,0.1)", border: errors.phone ? "1px solid #EF4444" : "1px solid rgba(124,58,237,0.3)" }}
                    onFocus={(e) => (e.target.style.border = "1px solid rgba(124,58,237,0.8)")}
                    onBlur={(e) => (e.target.style.border = errors.phone ? "1px solid #EF4444" : "1px solid rgba(124,58,237,0.3)")}
                  />
                  {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: "rgba(167,139,250,0.9)" }}>
                    <Mail size={13} className="inline mr-1.5" />
                    Мэйл хаяг <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@gmail.com"
                    className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all"
                    style={{ background: "rgba(124,58,237,0.1)", border: errors.email ? "1px solid #EF4444" : "1px solid rgba(124,58,237,0.3)" }}
                    onFocus={(e) => (e.target.style.border = "1px solid rgba(124,58,237,0.8)")}
                    onBlur={(e) => (e.target.style.border = errors.email ? "1px solid #EF4444" : "1px solid rgba(124,58,237,0.3)")}
                  />
                  {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                  <p className="text-xs mt-1" style={{ color: "rgba(167,139,250,0.5)" }}>
                    Зөвлөгөөний баталгаажуулалт мэйлээр явуулна
                  </p>
                </div>

                <div className="rounded-xl p-3 text-xs" style={{ background: "rgba(124,58,237,0.08)", border: "1px solid rgba(124,58,237,0.2)", color: "rgba(167,139,250,0.7)" }}>
                  🔒 Таны мэдээлэл зөвхөн зөвлөгөө авах зорилгоор ашиглагдана.
                </div>
              </motion.div>
            )}

            {/* Step 2: Country */}
            {step === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <div className="grid grid-cols-2 gap-2.5">
                  {COUNTRIES.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => handleCountrySelect(c.id)}
                      className="relative flex items-center gap-3 px-4 py-3.5 rounded-xl text-left transition-all hover:scale-[1.02]"
                      style={c.trending ? { background: "rgba(251,146,60,0.12)", border: "1px solid rgba(251,146,60,0.45)", color: "white" } : { background: "rgba(124,58,237,0.1)", border: "1px solid rgba(124,58,237,0.25)", color: "white" }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = c.trending ? "rgba(251,146,60,0.22)" : "rgba(124,58,237,0.25)";
                        (e.currentTarget as HTMLButtonElement).style.border = c.trending ? "1px solid rgba(251,146,60,0.7)" : "1px solid rgba(124,58,237,0.6)";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = c.trending ? "rgba(251,146,60,0.12)" : "rgba(124,58,237,0.1)";
                        (e.currentTarget as HTMLButtonElement).style.border = c.trending ? "1px solid rgba(251,146,60,0.45)" : "1px solid rgba(124,58,237,0.25)";
                      }}
                    >
                      {c.trending && (
                        <span className="absolute -top-2 -right-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "linear-gradient(135deg, #f97316, #fb923c)", color: "white" }}>🔥 Trending</span>
                      )}
                      <span className="text-2xl">{c.flag}</span>
                      <span className="font-medium text-sm">{c.name}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 3: Services */}
            {step === 3 && (
              <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-3">
                {/* Discount notice */}
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl" style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)" }}>
                  <span className="text-base">💡</span>
                  <p className="text-xs leading-snug" style={{ color: "rgba(110,231,183,0.9)" }}>
                    Үйлчилгээ авбал зөвлөгөөний төлбөр <strong style={{ color: "#6EE7B7" }}>хасагдана</strong>
                  </p>
                </div>
                {services.map((service) => {
                  const isVip = service.highlight === "vip";
                  const isWork = service.highlight === "work";
                  const isSpecial = service.highlight === "special";
                  const bgColor = isVip ? "rgba(234,179,8,0.07)" : isWork ? "rgba(16,185,129,0.07)" : isSpecial ? "rgba(249,115,22,0.07)" : "rgba(124,58,237,0.06)";
                  const borderColor = isVip ? "1px solid rgba(234,179,8,0.3)" : isWork ? "1px solid rgba(16,185,129,0.3)" : isSpecial ? "1px solid rgba(249,115,22,0.3)" : "1px solid rgba(124,58,237,0.2)";
                  const bgHover = isVip ? "rgba(234,179,8,0.12)" : isWork ? "rgba(16,185,129,0.12)" : isSpecial ? "rgba(249,115,22,0.12)" : "rgba(124,58,237,0.12)";
                  const borderHover = isVip ? "1px solid rgba(234,179,8,0.5)" : isWork ? "1px solid rgba(16,185,129,0.5)" : isSpecial ? "1px solid rgba(249,115,22,0.5)" : "1px solid rgba(124,58,237,0.5)";
                  const accentColor = isVip ? "#FBBF24" : isWork ? "#34D399" : isSpecial ? "#FB923C" : "#A78BFA";
                  const checkColor = isVip ? "#FBBF24" : isWork ? "#34D399" : isSpecial ? "#FB923C" : "#A78BFA";
                  const badgeIcon = isVip ? <Star size={12} className="text-yellow-400 flex-shrink-0" /> : isWork ? <span style={{ color: "#34D399", fontSize: 13 }}>💼</span> : isSpecial ? <span style={{ color: "#FB923C", fontSize: 13 }}>⭐</span> : null;
                  const previewBullets = service.bullets.slice(0, 4);
                  const hasMore = service.bullets.length > 4;
                  return (
                    <div
                      key={service.id}
                      className="rounded-xl p-4 transition-all"
                      style={{ background: bgColor, border: borderColor }}
                    >
                      {/* Header row: name + price */}
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-1.5 flex-1 min-w-0">
                          {badgeIcon}
                          <span className="font-semibold text-white text-sm leading-snug">{service.name}</span>
                        </div>
                        <span className="font-bold text-sm flex-shrink-0" style={{ color: accentColor }}>₮{service.price.toLocaleString()}</span>
                      </div>
                      {/* Preview bullets */}
                      <ul className="space-y-1 mb-3">
                        {previewBullets.map((b, i) => (
                          <li key={i} className="flex items-start gap-1.5">
                            <span className="text-xs flex-shrink-0 mt-0.5" style={{ color: checkColor }}>✓</span>
                            <span className="text-xs leading-snug" style={{ color: "rgba(200,185,255,0.75)" }}>{b}</span>
                          </li>
                        ))}
                      </ul>
                      {/* Footer: more hint + detail button */}
                      <div className="flex items-center justify-between">
                        {hasMore && (
                          <span className="text-xs" style={{ color: "rgba(167,139,250,0.45)" }}>+{service.bullets.length - 4} дэлгэрэнгүй</span>
                        )}
                        <button
                          onClick={() => navigate(`/consultation/${selectedCountry}/${service.id}`)}
                          className="ml-auto text-xs font-medium px-3 py-1 rounded-lg transition-all hover:opacity-80"
                          style={{ background: bgHover, color: accentColor, border: borderHover }}
                        >
                          Дэлгэрэнгүй →
                        </button>
                      </div>
                    </div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-3" style={{ borderTop: "1px solid rgba(124,58,237,0.15)" }}>
          <div className="flex gap-3">
            {step > 1 && (
              <button
                onClick={() => {
                  if (step === 3) { setSelectedService(null); setStep(2); }
                  else setStep(step - 1);
                }}
                className="flex-1 py-3 rounded-xl font-medium transition-all"
                style={{ background: "rgba(124,58,237,0.1)", border: "1px solid rgba(124,58,237,0.3)", color: "rgba(167,139,250,0.9)" }}
              >
                ← Буцах
              </button>
            )}

            {step === 1 && (
              <button
                onClick={handleStep1Next}
                className="flex-1 py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition-all hover:opacity-90"
                style={{ background: "linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)" }}
              >
                Үргэлжлүүлэх <ChevronRight size={16} />
              </button>
            )}

            {step === 2 && (
              <div className="flex-1 text-center text-sm py-3" style={{ color: "rgba(167,139,250,0.6)" }}>
                <Globe size={14} className="inline mr-1.5" />
                Улсаа сонгоно уу
              </div>
            )}

            {step === 3 && !selectedService && (
              <div className="flex-1 text-center text-sm py-3" style={{ color: "rgba(167,139,250,0.6)" }}>
                Үйлчилгээ сонгоно уу
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
